import {
  CalendarHubClient,
  CreateMsEventDto,
  MsEventDto,
  msEventEditableProperties,
  MsEventEditableProperties,
} from "@clients/calendarHubClient"
import {
  MergedCopyChanges,
  MsEventProps,
} from "@masterschool/ui-calendar/dist/src/types"
import { useState, useEffect, useCallback } from "react"
import {
  mapToMsEventDto as mapToMsCreateEventDto,
  mapToMsEventProps,
} from "./calendarMappers"
import { DateRange, getDateRangeWithPlusMinusMonth } from "@utils/date"
import { useDebounce } from "@utils/hooks/useDebounce"
import { useIsCurrentTabActive } from "@utils/hooks/useIsCurrentTabActive"

const AutoRefreshIntervalMS = 5 * 10000

// Lightweight record of a folded duplicate copy, kept on the survivor so the
// edit sidebar can list each copy and reassign its instructor.
const mergedEventOf = (event: MsEventDto) => ({
  id: event.id,
  title: event.title,
  instructorCalendarId: event.instructorCalendarId,
  description: event.description,
  type: event.type,
  participantCalendarIds: event.participants,
})

const useFetchEvents = (
  calendarIds: string[],
  dateRange: { start: Date; end: Date },
  setEvents: (events: MsEventProps[]) => void,
  fetchOrganized: boolean,
) => {
  const { start: queryStartDate, end: queryEndDate } = dateRange

  const fetchEvents = useCallback(async () => {
    // Drafts have no calendar attachment, so they must show regardless of which
    // calendars are selected — otherwise a just-saved draft vanishes when no
    // calendar is selected. Only the calendar-filtered events need calendarIds.
    const events =
      calendarIds.length === 0
        ? []
        : await CalendarHubClient.getEvents(
            calendarIds,
            queryStartDate,
            queryEndDate,
          )

    const draftEvents = await CalendarHubClient.getDrafts(
      queryStartDate,
      queryEndDate,
    )

    // Events the user organized, shown regardless of calendar selection when the
    // "All organized by me" toggle is on. Merged into the dedupe pool below so
    // an event already on a selected calendar isn't shown twice.
    const organizedEvents = fetchOrganized
      ? await CalendarHubClient.getOrganized(queryStartDate, queryEndDate)
      : []

    // A no-VC custom "invitation wrapper" carries the real session's join link
    // in its description (…/campus/event/{live-session-id}/vc). That linked
    // live-session id is the true session identity; a live-session's identity is
    // its own id.
    const linkedSessionId = (event: MsEventDto) => {
      if (event.type === "live-session") {
        return event.id
      }
      if (event.type === "regular" && !event.hasVc) {
        const description = event.description ?? ""
        // (?:@\d+)? skips the recurring @{startMs} suffix in …/event/{id}@{ts}/vc
        const campusSessionId = description.match(
          /campus\/event\/([0-9a-f-]+)(?:@\d+)?\/vc/,
        )?.[1]
        if (campusSessionId) {
          return campusSessionId
        }
        // Tutors also paste the raw meeting link straight into the description
        // (e.g. zoom.us/j/{meetingId}); the meeting id is the session identity.
        // Prefixed so it can't collide with a campus event id. The merge still
        // requires identical start (below), so two different sessions reusing a
        // recurring room never fold together.
        const zoomMeetingId = description.match(/zoom\.us\/j\/(\d+)/)?.[1]
        if (zoomMeetingId) {
          return `zoom:${zoomMeetingId}`
        }
      }
      return undefined
    }

    // The dedup keys an event can be matched by, in the same precedence
    // isSameLiveSession uses (linked-session > instructor+slot > recurring group
    // > id). Indexing survivors by these keys turns the per-event survivor scan
    // (O(n²) on big multi-calendar views) into an O(1) lookup; only a survivor's
    // own identity is indexed, so folding never widens it and the result matches
    // the previous greedy dedup.
    const matchKeys = (event: MsEventDto): string[] => {
      const start = `${event.start}`
      const keys: string[] = []
      const session = linkedSessionId(event)
      if (session) {
        keys.push(`link:${session}|${start}`)
      }
      if (event.type === "live-session" && event.instructorCalendarId) {
        keys.push(`instr:${event.instructorCalendarId}|${start}`)
      }
      if (event.recurringGroupID) {
        keys.push(`rg:${event.recurringGroupID}|${start}`)
      }
      keys.push(`id:${event.id}|${start}`)
      return keys
    }

    const sortedEvents = [...events, ...organizedEvents].sort(
      (a, b) =>
        // Keep the copy that carries the meeting link first, so the surviving
        // deduped event is the one the instructor can actually join. Calendar
        // events sort ahead of the 'organized' copies so a selected calendar
        // keeps the event on its own colour rather than the organized group.
        Number(b.hasVc) - Number(a.hasVc) ||
        b.participants.length - a.participants.length ||
        (b.description?.length ?? 0) - (a.description?.length ?? 0),
    )

    const uniqueEvents: MsEventDto[] = []
    const survivorByKey = new Map<string, MsEventDto>()
    // Distinct event ids already folded into each survivor. The SAME event can
    // arrive several times in one fetch — get-organized returns one row per
    // ms_calendar_to_event link, so an organized event invited to N calendars
    // comes back N times (same id, same slot), and the 'organized' copy of an
    // event already on a selected calendar adds one more. Those are one event
    // shown once, not a merge. Keying the merged set by event id means same-id
    // duplicates fold for display but count once — only a DISTINCT event id is a
    // real merge (the "one wrapper per invitee" workaround). Without this a
    // single event on >1 calendar shows a false "+N" badge / "N copies share a
    // meeting" sidebar.
    const mergedIdsBySurvivor = new Map<MsEventDto, Set<string>>()
    // Distinct calendar ids folded into each survivor — backs calendarMemberships
    // so the same calendar isn't recorded twice.
    const membershipCalsBySurvivor = new Map<MsEventDto, Set<string>>()
    for (const event of sortedEvents) {
      const keys = matchKeys(event)
      let survivor: MsEventDto | undefined
      for (const key of keys) {
        const candidate = survivorByKey.get(key)
        if (candidate) {
          survivor = candidate
          break
        }
      }

      if (!survivor) {
        const newSurvivor: MsEventDto = {
          ...event,
          participants: [...event.participants],
          mergedTitles: [event.title],
          mergedEvents: [mergedEventOf(event)],
          calendarMemberships: [
            { calendarId: event.calendarId, color: event.color },
          ],
        }
        uniqueEvents.push(newSurvivor)
        mergedIdsBySurvivor.set(newSurvivor, new Set([event.id]))
        membershipCalsBySurvivor.set(newSurvivor, new Set([event.calendarId]))
        for (const key of keys) {
          if (!survivorByKey.has(key)) {
            survivorByKey.set(key, newSurvivor)
          }
        }
        continue
      }

      // Index this folded copy's own keys onto the survivor too, so a later
      // event that references THIS copy still finds the merged group. Without
      // it, when several identical live sessions share a slot+instructor only
      // one becomes the survivor; a custom "invitation wrapper" whose
      // description links to one of the FOLDED sessions (not the survivor)
      // couldn't find the group and was shown as a separate event. The keys
      // carry the exact start, and link:/id: are event-id-specific, so this
      // never merges across slots or unrelated events.
      for (const key of keys) {
        if (!survivorByKey.has(key)) {
          survivorByKey.set(key, survivor)
        }
      }
      // Always fold the copy's invitees onto the surviving event so the single
      // shown event keeps the full invitee list (harmless for same-id duplicates,
      // which carry the same roster).
      survivor.participants = Array.from(
        new Set([...survivor.participants, ...event.participants]),
      )
      // Record every calendar this event is on (one per folded copy) so the
      // calendar can keep it visible + correctly coloured when only some of its
      // calendars are selected; "organized" is just one more membership. A
      // selected real calendar is matched before "organized" (real copies fold
      // first), so it keeps its colour and only goes white when none is selected.
      const memberCals =
        membershipCalsBySurvivor.get(survivor) ?? new Set<string>()
      membershipCalsBySurvivor.set(survivor, memberCals)
      if (!memberCals.has(event.calendarId)) {
        memberCals.add(event.calendarId)
        survivor.calendarMemberships = [
          ...(survivor.calendarMemberships ?? []),
          { calendarId: event.calendarId, color: event.color },
        ]
      }
      // Only a genuinely distinct event id is a real merge. The same event id
      // arriving again (same event linked to another calendar, or its 'organized'
      // copy) is one event, so it must not inflate the merged set.
      const mergedIds =
        mergedIdsBySurvivor.get(survivor) ?? new Set<string>([survivor.id])
      mergedIdsBySurvivor.set(survivor, mergedIds)
      if (!mergedIds.has(event.id)) {
        mergedIds.add(event.id)
        // Keep every distinct title of the folded copies so the UI can show a
        // "+N" badge + the full list (duplicates often have inconsistent titles).
        survivor.mergedTitles = Array.from(
          new Set([
            ...(survivor.mergedTitles ?? [survivor.title]),
            event.title,
          ]),
        )
        // Keep each folded copy (id/title/instructor) so the edit sidebar can
        // reassign one copy's instructor to split it back out.
        survivor.mergedEvents = [
          ...(survivor.mergedEvents ?? [mergedEventOf(survivor)]),
          mergedEventOf(event),
        ]
      }
    }

    setEvents([...uniqueEvents, ...draftEvents].map(mapToMsEventProps))
  }, [calendarIds, queryStartDate, queryEndDate, setEvents, fetchOrganized])

  return fetchEvents
}

const useHandleEventEdit = () => {
  return async (
    data: {
      oldEvent: MsEventProps
      newEvent: MsEventProps
    },
    recurringTarget?: "this" | "this-and-future" | "all",
  ) => {
    const isRecurring = data.oldEvent.recurringId
    if (isRecurring) {
      assertHaveRecurringTarget(recurringTarget)
      return updateRecurringEvent(data, recurringTarget)
    } else {
      return updateOneTimeEvent(data)
    }
  }
}

const useHandleDeleteEvent =
  () =>
  (
    event: MsEventProps,
    recurringTarget?: "this" | "this-and-future" | "all",
  ) => {
    const isRecurring = event.recurringId
    if (isRecurring) {
      assertHaveRecurringTarget(recurringTarget)
      return deleteRecurringEvent(event, recurringTarget)
    } else {
      return CalendarHubClient.delete(event.id)
    }
  }

function assertHaveRecurringTarget(
  recurringTarget: "this" | "this-and-future" | "all" | undefined,
): asserts recurringTarget is "all" {
  if (!recurringTarget) {
    throw new Error(
      "recurringTarget is required when editing a recurring event",
    )
  }
}

export const useEventActions = (
  calendarIdsFetchUpfront: string[],
  dateRange: DateRange,
  onDatesChange: (dateRange: DateRange) => void,
) => {
  const [calendarIdsForEventFetch, setCalendarIdsForEventFetch] = useState<
    string[]
  >(calendarIdsFetchUpfront)

  const [fetchOrganized, setFetchOrganized] = useState(false)
  const [msEvents, setMsEvents] = useState<MsEventProps[]>([])
  const [loading, setLoading] = useState(false)

  const fetchEvents = useFetchEvents(
    calendarIdsForEventFetch,
    dateRange,
    setMsEvents,
    fetchOrganized,
  )
  const editEvents = useHandleEventEdit()
  const deleteEvent = useHandleDeleteEvent()
  const isCurrentTabActive = useIsCurrentTabActive()
  const onDatesChangeHandler = useCallback(
    (from: Date, to: Date) => {
      const adjustedDateRange = getDateRangeWithPlusMinusMonth(from, to)
      onDatesChange(adjustedDateRange)
    },
    [onDatesChange],
  )

  const onCalendarDatesChange = useDebounce(onDatesChangeHandler, 300)

  const addCalendarIdsToFetch = (calendarIds: string[]) => {
    const hasNewCalendar = calendarIds.some(
      (calendarId) => !calendarIdsForEventFetch.includes(calendarId),
    )
    if (!hasNewCalendar) {
      return
    }
    const mergedListOfCalendars = [
      ...new Set([...calendarIdsForEventFetch, ...calendarIds]),
    ]

    setCalendarIdsForEventFetch(mergedListOfCalendars)
  }

  useEffect(() => {
    setLoading(true)
    fetchEvents().then(() => setLoading(false))
  }, [fetchEvents])

  useEffect(() => {
    let lastRefreshed = new Date()
    const interval = setInterval(async () => {
      if (
        isCurrentTabActive &&
        new Date().getTime() - lastRefreshed.getTime() > AutoRefreshIntervalMS
      ) {
        await fetchEvents()
        lastRefreshed = new Date()
      }
    }, AutoRefreshIntervalMS)

    return () => clearInterval(interval)
  }, [fetchEvents, isCurrentTabActive])

  const handleEventAction = async (
    event: MsEventProps,
    index: number | null,
    recurringTarget?: "this" | "this-and-future" | "all",
  ) => {
    if (index === null) {
      await createNewEvent(event)
    } else {
      const oldEvent = msEvents[index]
      if (oldEvent.id !== event.id) {
        throw new Error("Event id mismatch")
      }

      await editEvents(
        {
          oldEvent,
          newEvent: event,
        },
        recurringTarget,
      )
    }
  }

  const onChange = async (
    event: MsEventProps,
    index: number | null,
    recurringTarget?: "this" | "this-and-future" | "all",
  ) => {
    await handleEventAction(event, index, recurringTarget)
    await fetchEvents()
  }

  const onDelete = async (
    event: MsEventProps,
    index: number,
    recurringTarget?: "this" | "this-and-future" | "all",
  ) => {
    const eventToDelete = msEvents[index]
    if (eventToDelete.id !== event.id) {
      throw new Error("Event id mismatch")
    }

    await deleteEvent(eventToDelete, recurringTarget)
    await fetchEvents()
  }

  // Reassign a single merged duplicate copy's instructor (by id). The dedup keys
  // on instructor+start, so a new instructor splits that copy back out on the
  // next fetch.
  // Persist edits to one merged copy (by id) — title, participants, instructor
  // and/or description — in a single update. Changing the instructor or the
  // …/event/{id}/vc link in the description splits that copy back out of the
  // merge on the next fetch.
  const onMergedCopySave = async (
    eventId: string,
    changes: MergedCopyChanges,
  ) => {
    const { participantCalendarIds, ...rest } = changes
    await CalendarHubClient.edit(eventId, {
      ...rest,
      ...(participantCalendarIds
        ? { participants: participantCalendarIds }
        : {}),
    })
    await fetchEvents()
  }

  return {
    msEvents,
    onChange,
    onDelete,
    addCalendarIdsToFetch,
    setFetchOrganized,
    onCalendarDatesChange,
    onMergedCopySave,
    loading,
  }
}

async function updateOneTimeEvent(data: {
  oldEvent: MsEventProps
  newEvent: MsEventProps
}) {
  const editedProperties = getChangedFields(data.oldEvent, data.newEvent)
  return CalendarHubClient.edit(data.oldEvent.id, editedProperties)
}

async function updateRecurringEvent(
  data: { oldEvent: MsEventProps; newEvent: MsEventProps },
  recurringTarget: "this" | "this-and-future" | "all",
) {
  const target = {
    event_id: data.oldEvent.id,
    occurrence_start_time: data.oldEvent.start,
    recurring_group_id: data.oldEvent.recurringId,
    type: recurringTarget,
  }
  const editedProperties = getChangedFields(data.oldEvent, data.newEvent)
  return CalendarHubClient.editRecurring(target, editedProperties)
}

async function deleteRecurringEvent(
  event: MsEventProps,
  recurringTarget: "this" | "this-and-future" | "all",
) {
  return CalendarHubClient.deleteRecurring(
    event.id,
    event.start,
    event.recurringId || "",
    recurringTarget,
  )
}

async function createNewEvent(event: MsEventProps) {
  await CalendarHubClient.create(mapToMsCreateEventDto(event))
}

function getChangedFields(
  originalEventProps: MsEventProps,
  newEventProps: MsEventProps,
) {
  const originalEventDto = mapToMsCreateEventDto(originalEventProps)
  const newEventDto = mapToMsCreateEventDto(newEventProps)
  const changedFields = {} as Partial<MsEventEditableProperties>

  msEventEditableProperties.forEach((key) => {
    pushPropertyIfChanged(changedFields, key, originalEventDto, newEventDto)
  })

  const isRecurring = !!newEventProps.recurringRule
  if (isRecurring) {
    addTzidIfAndOnlyIfRRuleChanged(
      changedFields,
      newEventProps.recurringRuleTzid,
    )
  }

  return changedFields
}

function pushPropertyIfChanged<K extends keyof MsEventEditableProperties>(
  changedFields: Partial<MsEventEditableProperties>,
  key: K,
  originalEvent: CreateMsEventDto,
  newEvent: CreateMsEventDto,
) {
  const originalValue = originalEvent[key]
  const newValue = newEvent[key]

  switch (key) {
    case "participants":
      if (
        !arraysEqualsUnordered(
          originalEvent.participants,
          newEvent.participants,
        )
      ) {
        changedFields.participants = newEvent.participants
      }
      break
    case "start":
    case "end":
    case "rruleUntil":
      if (!isDatesEqual(originalValue, newValue)) {
        changedFields[key] = newEvent[key]
      }
      break
    default:
      if (originalValue !== newValue) {
        changedFields[key] = newEvent[key]
      }
  }
}

function arraysEqualsUnordered(a: string[], b: string[]) {
  return JSON.stringify(a.sort()) === JSON.stringify(b.sort())
}

function isDatesEqual(date1: any, date2: any) {
  const isBothDateInstances = date1 instanceof Date && date2 instanceof Date

  return isBothDateInstances
    ? date1.getTime() === date2.getTime()
    : date1 === date2
}

function addTzidIfAndOnlyIfRRuleChanged(
  changedFields: Partial<MsEventEditableProperties>,
  rruleTzid: string,
) {
  const hasRrule = !!changedFields.rrule
  const hasTimezone = !!changedFields.rruleTzid
  if (hasRrule && !hasTimezone) {
    changedFields.rruleTzid = rruleTzid
  }
  if (hasTimezone && !hasRrule) {
    // just a defensive code
    // if event created in israel and someone in germany edited it - we don't want to accidentally update the timezone.
    delete changedFields["rruleTzid"]
  }
}
